import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Star, Clock } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { getArticle, getArticlesByCategory } from '../data/articles';
import Footer from './Footer';

const ArticlesPage = ({ showPage }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  // Handle URL parameters for category filtering
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    const search = params.get('search');
    
    if (category && ['all', 'getting-started', 'electricity', 'payments', 'maintenance', 'account', 'community'].includes(category)) {
      setSelectedCategory(category);
    }
    
    if (search) {
      setSearchQuery(decodeURIComponent(search));
    }
  }, [location.search]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedArticle(null); // Reset selected article when changing category
  };

  const handleArticleClick = (articleId) => {
    const article = getArticle(articleId);
    if (article) {
      setSelectedArticle(article);
    }
  };

  const handleBackToArticles = () => {
    setSelectedArticle(null);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Filter articles based on search query and category
  const filteredArticles = () => {
    let articles = getArticlesByCategory(selectedCategory);
    if (searchQuery.trim()) {
      articles = articles.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return articles;
  };

  // Helper function to get category styling
  const getCategoryStyle = (category) => {
    const styles = {
      'electricity': { icon: '⚡', color: 'text-amber-600', borderColor: 'border-amber-200', bgColor: 'bg-amber-50' },
      'payments': { icon: '💳', color: 'text-blue-600', borderColor: 'border-blue-200', bgColor: 'bg-blue-50' },
      'getting-started': { icon: '🚀', color: 'text-green-600', borderColor: 'border-green-200', bgColor: 'bg-green-50' },
      'maintenance': { icon: '🔧', color: 'text-green-600', borderColor: 'border-green-200', bgColor: 'bg-green-50' },
      'account': { icon: '🔐', color: 'text-purple-600', borderColor: 'border-purple-200', bgColor: 'bg-purple-50' },
      'community': { icon: '👥', color: 'text-indigo-600', borderColor: 'border-indigo-200', bgColor: 'bg-indigo-50' }
    };
    return styles[category] || styles['getting-started'];
  };

  // Universal article card component
  const ArticleCard = ({ article }) => {
    const categoryStyle = getCategoryStyle(article.category);
    const isPopular = ['buy-electricity', 'token-not-working', 'autopay', 'getting-started'].includes(article.id);
    
    return (
      <div 
        onClick={() => handleArticleClick(article.id)}
        className={`article-card visible block w-full text-left p-5 rounded-large border-2 ${
          isPopular ? `${categoryStyle.borderColor} ${categoryStyle.bgColor}` : 'border-neutral-200'
        } hover:border-primary-300 hover:shadow-custom transition-all group cursor-pointer`}
      >
        <div className="flex items-center gap-2 mb-2">
          <span className={`${categoryStyle.color} text-sm`}>{categoryStyle.icon}</span>
          <span className={`text-xs font-semibold ${categoryStyle.color} uppercase`}>
            {article.category.replace('-', ' ')}
          </span>
        </div>
        <h3 className={`font-heading font-bold text-neutral-900 mb-2 group-hover:${
          article.category === 'electricity' ? 'text-amber-700' : 'text-primary-600'
        }`}>
          {article.title}
        </h3>
        <p className="text-sm text-neutral-500">{article.description}</p>
      </div>
    );
  };

  // Get articles for current category
  const currentArticles = filteredArticles();

  return (
    <div className="page active">
      {/* HEADER SECTION */}
      <section className="pt-16 pb-12 bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button 
            onClick={() => showPage('/support')}
            className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-primary-600 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Support Center
          </button>
          <div className="text-center mb-12">
            <h1 className="font-heading text-4xl font-bold text-neutral-900 mb-4">Help Center</h1>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Find answers to common questions about Bilm's features and services
            </p>
          </div>
          <div className="max-w-md mx-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
              <input 
                type="text" 
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search for help articles..."
                className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-large focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLE DETAIL VIEW */}
      {selectedArticle && (
        <div id="article-detail-view">
          <section className="pt-16 pb-12 bg-neutral-50 border-b border-neutral-200">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <button 
                onClick={handleBackToArticles}
                className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-primary-600 mb-8 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Back to Help Articles
              </button>
              <div id="article-category-badge" className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${
                selectedArticle.category === 'Electricity' ? 'bg-amber-100 text-amber-700' :
                selectedArticle.category === 'Payments' ? 'bg-blue-100 text-blue-700' :
                selectedArticle.category === 'Getting Started' ? 'bg-green-100 text-green-700' :
                selectedArticle.category === 'Maintenance' ? 'bg-green-100 text-green-700' :
                selectedArticle.category === 'Account & Security' ? 'bg-purple-100 text-purple-700' :
                selectedArticle.category === 'Community' ? 'bg-indigo-100 text-indigo-700' :
                'bg-neutral-100 text-neutral-700'
              }`}>
                {selectedArticle.category}
              </div>
              <h1 id="article-title" className="font-heading text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">
                {selectedArticle.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-neutral-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span id="article-read-time">{selectedArticle.readTime}</span>
                </span>
              </div>
            </div>
          </section>
          <section className="py-12 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div id="article-body" className="prose max-w-none text-neutral-700 leading-relaxed" dangerouslySetInnerHTML={{__html: selectedArticle.content}} />
            </div>
          </section>
        </div>
      )}

      {/* ARTICLES CONTENT - HIDE WHEN ARTICLE IS SELECTED */}
      {!selectedArticle && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-4 gap-12">
              {/* CATEGORIES SIDEBAR */}
              <div className="lg:col-span-1">
                <div className="sticky top-6">
                  <h3 className="font-heading text-lg font-bold text-neutral-900 mb-6">Categories</h3>
                  <nav className="space-y-1">
                    <ul className="space-y-1">
                      <li>
                        <button 
                          onClick={() => handleCategoryClick('all')}
                          className={`w-full text-left px-3 py-2 rounded-small text-sm font-medium transition-colors ${
                            selectedCategory === 'all' 
                              ? 'bg-primary-50 text-primary-700' 
                              : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                        >
                          All Articles
                        </button>
                      </li>
                      <li>
                        <button 
                          onClick={() => handleCategoryClick('getting-started')}
                          className={`w-full text-left px-3 py-2 rounded-small text-sm font-medium transition-colors ${
                            selectedCategory === 'getting-started' 
                              ? 'bg-primary-50 text-primary-700' 
                              : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                        >
                          Getting Started
                        </button>
                      </li>
                      <li>
                        <button 
                          onClick={() => handleCategoryClick('electricity')}
                          className={`w-full text-left px-3 py-2 rounded-small text-sm font-medium transition-colors ${
                            selectedCategory === 'electricity' 
                              ? 'bg-primary-50 text-primary-700' 
                              : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                        >
                          Electricity
                        </button>
                      </li>
                      <li>
                        <button 
                          onClick={() => handleCategoryClick('payments')}
                          className={`w-full text-left px-3 py-2 rounded-small text-sm font-medium transition-colors ${
                            selectedCategory === 'payments' 
                              ? 'bg-primary-50 text-primary-700' 
                              : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                        >
                          Payments
                        </button>
                      </li>
                      <li>
                        <button 
                          onClick={() => handleCategoryClick('maintenance')}
                          className={`w-full text-left px-3 py-2 rounded-small text-sm font-medium transition-colors ${
                            selectedCategory === 'maintenance' 
                              ? 'bg-primary-50 text-primary-700' 
                              : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                        >
                          Maintenance
                        </button>
                      </li>
                      <li>
                        <button 
                          onClick={() => handleCategoryClick('account')}
                          className={`w-full text-left px-3 py-2 rounded-small text-sm font-medium transition-colors ${
                            selectedCategory === 'account' 
                              ? 'bg-primary-50 text-primary-700' 
                              : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                        >
                          Account & Security
                        </button>
                      </li>
                      <li>
                        <button 
                          onClick={() => handleCategoryClick('community')}
                          className={`w-full text-left px-3 py-2 rounded-small text-sm font-medium transition-colors ${
                            selectedCategory === 'community' 
                              ? 'bg-primary-50 text-primary-700' 
                              : 'text-neutral-600 hover:bg-primary-50 hover:text-primary-700'
                          }`}
                        >
                          Community
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>

              {/* ARTICLES LIST */}
              <div className="lg:col-span-3">
                {/* POPULAR ARTICLES */}
                <div className="mb-12">
                  <h2 className="font-heading text-xl font-bold text-neutral-900 mb-6 flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400" /> Popular Articles
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {currentArticles.filter(article => 
                      ['buy-electricity', 'token-not-working', 'autopay', 'getting-started'].includes(article.id)
                    ).map(article => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                </div>

                {/* ALL ARTICLES IN CATEGORY */}
                <div>
                  <h2 className="font-heading text-xl font-bold text-neutral-900 mb-6">
                    {selectedCategory === 'all' ? 'All Articles' : `${selectedCategory.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())} Articles`}
                  </h2>
                  <div className="space-y-4">
                    {currentArticles.map(article => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      
      <Footer showPage={showPage} />
    </div>
  );
};

export default ArticlesPage;
